// File path: src/hooks/coinglass/useTokenPriceData.ts

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { tokenPriceHistoryQueryOptions } from "@/services/queries/coinglass/coinglassQueries";
import { dateIntToTimestamp } from "@/utils/coinglass";

/**
 * Price data point structure
 */
export interface PriceDataPoint {
  timestamp: number;
  price: number | null;
}

/**
 * Custom hook for fetching token price history data
 *
 * @param ticker - Token ticker symbol (e.g., "BTC")
 * @returns Query result with processed price data
 */
export function useTokenPriceData(ticker: string) {
  const query = useQuery(tokenPriceHistoryQueryOptions({ ticker }));

  // Process raw API data into chart-ready format
  const processedData = useMemo<PriceDataPoint[]>(() => {
    if (!query.data) return [];

    const apiData = query.data;
    const priceMetric = apiData.metrics.find(
      (m) => m.metric_key === "price_close"
    );

    if (!priceMetric) return [];

    const { dates, values } = priceMetric;

    return dates.map((dateInt, index) => ({
      timestamp: dateIntToTimestamp(dateInt),
      price: values[index] ?? null,
    }));
  }, [query.data]);

  return {
    ...query,
    data: processedData,
  };
}
